import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { selectUserId, selectIsAuthenticated } from '../store/userSlice';
import { NotificationControllerService } from '../api/services/NotificationControllerService';
import { usePushNotifications } from '../hooks/usePushNotifications';

const NotificationContext = createContext(null);

const PUSH_BANNER_DISMISSED_KEY = 'pushBannerDismissed';

const MIN_POLL_INTERVAL = 10_000;   // 10s — aktywny user
const MAX_POLL_INTERVAL = 300_000;  // 5 min — idle user
const BACKOFF_MULTIPLIER = 1.5;

export const NotificationProvider = ({ children }) => {
  const userId = useSelector(selectUserId);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const location = useLocation();
  const PAGE_SIZE = 20;
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const { isSupported, isSubscribed, permission, subscribe } = usePushNotifications();
  const [showPushBanner, setShowPushBanner] = useState(false);

  // Pokaż banner jeśli user nie ma subskrypcji push
  useEffect(() => {
    if (!isAuthenticated || !isSupported || isSubscribed) {
      setShowPushBanner(false);
      return;
    }

    // Zgoda już jest, ale brak subskrypcji — zasubskrybuj automatycznie
    if (permission === 'granted') {
      subscribe();
      return;
    }

    // Brak zgody i nie zablokowano — pokaż banner
    if (permission === 'default') {
      const dismissed = sessionStorage.getItem(PUSH_BANNER_DISMISSED_KEY);
      if (!dismissed) {
        setShowPushBanner(true);
      }
    }
  }, [isAuthenticated, isSupported, isSubscribed, permission]);

  const handleEnablePush = async () => {
    const result = await subscribe();
    setShowPushBanner(false);
    if (!result) {
      sessionStorage.setItem(PUSH_BANNER_DISMISSED_KEY, '1');
    }
  };

  const handleDismissPush = () => {
    setShowPushBanner(false);
    sessionStorage.setItem(PUSH_BANNER_DISMISSED_KEY, '1');
  };

  const pollIntervalRef = useRef(MIN_POLL_INTERVAL);
  const timerRef = useRef(null);
  const unreadCountRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const data = await NotificationControllerService.getNotifications(userId, { page: 0, size: PAGE_SIZE });
      const items = data.content || [];
      setNotifications(items);
      setCurrentPage(0);
      setHasMore(!data.last);
      const count = items.filter(n => !n.isRead).length;
      setUnreadCount(count);
      unreadCountRef.current = count;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchMoreNotifications = useCallback(async () => {
    if (!userId || loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      const data = await NotificationControllerService.getNotifications(userId, { page: nextPage, size: PAGE_SIZE });
      const items = data.content || [];
      setNotifications(prev => [...prev, ...items]);
      setCurrentPage(nextPage);
      setHasMore(!data.last);
    } catch (error) {
    } finally {
      setLoadingMore(false);
    }
  }, [userId, loadingMore, hasMore, currentPage]);

  const checkForUpdates = useCallback(async () => {
    if (!userId) return;

    try {
      const count = await NotificationControllerService.getUnreadCount(userId);

      if (count !== unreadCountRef.current) {
        await fetchNotifications();
        pollIntervalRef.current = MIN_POLL_INTERVAL;
      } else {
        pollIntervalRef.current = Math.min(
          pollIntervalRef.current * BACKOFF_MULTIPLIER,
          MAX_POLL_INTERVAL
        );
      }

      setUnreadCount(count);
      unreadCountRef.current = count;
    } catch (error) {
    }
  }, [userId, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    if (!userId) return false;

    try {
      await NotificationControllerService.markAsRead(userId, notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      return true;
    } catch (error) {
      return false;
    }
  };

  // Smart polling — adaptacyjny interval
  useEffect(() => {
    if (!userId) return;

    fetchNotifications();

    const schedulePoll = () => {
      timerRef.current = setTimeout(() => {
        checkForUpdates().finally(schedulePoll);
      }, pollIntervalRef.current);
    };

    schedulePoll();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [userId, fetchNotifications, checkForUpdates]);

  // Reset na krótki interval przy zmianie strony
  useEffect(() => {
    if (!userId) return;
    pollIntervalRef.current = MIN_POLL_INTERVAL;
    checkForUpdates();
  }, [location.pathname]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      loadingMore,
      hasMore,
      fetchNotifications,
      fetchMoreNotifications,
      markAsRead
    }}>
      {showPushBanner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          fontFamily: 'Inter, Arial, sans-serif',
          padding: '20px',
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '420px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>&#128276;</div>
            <h2 style={{
              margin: '0 0 12px 0',
              fontSize: '20px',
              fontWeight: 700,
              color: '#1a1a1a',
            }}>
              Powiadomienia
            </h2>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '14px',
              lineHeight: 1.6,
              color: '#555',
            }}>
              Włącz powiadomienia, aby otrzymywać informacje
              o nowych zapytaniach sparingowych, wiadomościach, odwołanych meczach
              i zmianach terminów — nawet gdy nie masz otwartej strony.
              <br /><br />
              Po kliknięciu <strong>Włącz</strong> przeglądarka zapyta Cię
              o zgodę — kliknij <strong>Zezwól</strong>, aby aktywować powiadomienia.
              <br /><br />
              Jeśli nie wyrazisz zgody, powiadomienia będą widoczne tylko po
              zalogowaniu się do aplikacji.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={handleEnablePush}
                style={{
                  backgroundColor: '#2e7d32',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 28px',
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: 'pointer',
                  flex: 1,
                }}
              >
                Włącz
              </button>
              <button
                onClick={handleDismissPush}
                style={{
                  backgroundColor: '#fff',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '12px 28px',
                  fontSize: '15px',
                  cursor: 'pointer',
                  flex: 1,
                }}
              >
                Nie teraz
              </button>
            </div>
          </div>
        </div>
      )}
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
